import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';

/**
 * A small Markdown renderer for assistant replies — the subset models actually
 * write: headings, paragraphs, lists (nested), code, quotes, tables, rules,
 * bold/italic/inline code and links.
 *
 * It builds React elements and never touches innerHTML, so model output can't
 * inject markup. Only static in-app paths are clickable (router links —
 * navigate without a reload, panel stays open). Every other link, external URLs
 * and mailto included, renders
 * as its label only: the persona already forbids them, and this holds even if a
 * prompt injection talks the model into one — a click must never carry data to
 * another site. Partial Markdown (mid-stream) renders sensibly because every
 * construct degrades to text.
 */

const LIST_ITEM = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/;
const RULE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const HEADING = /^(#{1,6})\s+(.*)$/;
const FENCE = /^\s*```/;
const QUOTE = /^\s*>\s?/;
/** Quotes and lists nest by recursion; past this depth the rest renders as text, so no reply can overflow the stack. */
const MAX_NESTING_DEPTH = 8;
/** Static in-app path — mirrors the server's link check. `//host` is excluded: it would leave the app. */
const APP_PATH = /^\/(?!\/)[A-Za-z0-9\-_/]*$/;
const startsBlock = (line) => FENCE.test(line) || HEADING.test(line) || QUOTE.test(line) || RULE.test(line) || LIST_ITEM.test(line);

const splitRow = (line) =>
    line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

function parseBlocks(source) {
    const lines = source.replace(/\r\n?/g, '\n').split('\n');
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        if (!line.trim()) {
            i++;
            continue;
        }

        if (FENCE.test(line)) {
            const body = [];
            i++;
            while (i < lines.length && !FENCE.test(lines[i])) body.push(lines[i++]);
            i++;
            blocks.push({ type: 'code', text: body.join('\n') });
            continue;
        }

        const heading = line.match(HEADING);
        if (heading) {
            blocks.push({ type: 'heading', level: heading[1].length, text: heading[2] });
            i++;
            continue;
        }

        if (RULE.test(line)) {
            blocks.push({ type: 'rule' });
            i++;
            continue;
        }

        if (QUOTE.test(line)) {
            const body = [];
            while (i < lines.length && QUOTE.test(lines[i])) body.push(lines[i++].replace(QUOTE, ''));
            blocks.push({ type: 'quote', text: body.join('\n') });
            continue;
        }

        if (line.includes('|') && TABLE_SEPARATOR.test((lines[i + 1] || '').trim())) {
            const header = splitRow(line);
            const rows = [];
            i += 2;
            while (i < lines.length && lines[i].includes('|') && lines[i].trim()) rows.push(splitRow(lines[i++]));
            blocks.push({ type: 'table', header, rows });
            continue;
        }

        const item = line.match(LIST_ITEM);
        if (item) {
            const indent = item[1].length;
            const ordered = /\d/.test(item[2]);
            const items = [];
            while (i < lines.length) {
                const current = lines[i];
                const match = current.match(LIST_ITEM);
                if (match && match[1].length <= indent) {
                    if (/\d/.test(match[2]) !== ordered) break;
                    items.push([match[3]]);
                    i++;
                } else if (current.trim() && /^\s+/.test(current) && items.length) {
                    // Continuation or nested list: dedent and let the item render it recursively.
                    items[items.length - 1].push(current.replace(new RegExp(`^\\s{0,${indent + 4}}`), ''));
                    i++;
                } else if (!current.trim() && LIST_ITEM.test(lines[i + 1] || '')) {
                    i++;
                } else {
                    break;
                }
            }
            blocks.push({ type: 'list', ordered, start: ordered ? parseInt(item[2], 10) : undefined, items: items.map((l) => l.join('\n')) });
            continue;
        }

        const body = [line];
        i++;
        while (i < lines.length && lines[i].trim() && !startsBlock(lines[i])) body.push(lines[i++]);
        blocks.push({ type: 'paragraph', lines: body });
    }

    return blocks;
}

const INLINE = String.raw`(\x60[^\x60\n]+\x60)|(\*\*[^*\n]+?\*\*)|(\[[^\]\n]+\]\([^)\s]+\))|(\*[^*\n]+?\*)|((?<![\w])_[^_\n]+?_(?![\w]))`;

function renderInline(text, keyPrefix) {
    const pattern = new RegExp(INLINE, 'g');
    const out = [];
    let last = 0;
    let match;
    let n = 0;

    while ((match = pattern.exec(text))) {
        const [token] = match;
        const key = `${keyPrefix}.${n++}`;
        if (match.index > last) out.push(text.slice(last, match.index));

        if (match[1]) {
            out.push(<code key={key}>{token.slice(1, -1)}</code>);
        } else if (match[2]) {
            out.push(<strong key={key}>{renderInline(token.slice(2, -2), key)}</strong>);
        } else if (match[3]) {
            const [, label, href] = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
            if (APP_PATH.test(href)) {
                out.push(
                    <Link key={key} to={href} className="md-app-link">
                        {label}
                    </Link>,
                );
            } else {
                out.push(label);
            }
        } else {
            out.push(<em key={key}>{renderInline(token.slice(1, -1), key)}</em>);
        }
        last = match.index + token.length;
    }

    if (last < text.length) out.push(text.slice(last));
    return out;
}

function renderBlock(block, key, depth) {
    switch (block.type) {
        case 'code':
            return (
                <pre key={key}>
                    <code>{block.text}</code>
                </pre>
            );
        case 'heading': {
            const Tag = `h${Math.min(block.level + 2, 6)}`;
            return <Tag key={key}>{renderInline(block.text, key)}</Tag>;
        }
        case 'rule':
            return <hr key={key} />;
        case 'quote':
            return <blockquote key={key}>{renderBlocks(block.text, key, depth + 1)}</blockquote>;
        case 'table':
            return (
                <div key={key} className="md-table">
                    <table>
                        <thead>
                            <tr>
                                {block.header.map((cell, c) => (
                                    <th key={c}>{renderInline(cell, `${key}.h${c}`)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {block.rows.map((row, r) => (
                                <tr key={r}>
                                    {block.header.map((_, c) => (
                                        <td key={c}>{renderInline(row[c] || '', `${key}.${r}.${c}`)}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';
            return (
                <Tag key={key} start={block.start}>
                    {block.items.map((itemText, n) => (
                        <li key={n}>{renderBlocks(itemText, `${key}.${n}`, depth + 1)}</li>
                    ))}
                </Tag>
            );
        }
        default:
            return (
                <p key={key}>
                    {block.lines.map((line, n) => (
                        <React.Fragment key={n}>
                            {n > 0 && <br />}
                            {renderInline(line, `${key}.${n}`)}
                        </React.Fragment>
                    ))}
                </p>
            );
    }
}

function renderBlocks(source, keyPrefix, depth = 0) {
    if (depth > MAX_NESTING_DEPTH) return [<p key={keyPrefix}>{source}</p>];
    return parseBlocks(source).map((block, n) => renderBlock(block, `${keyPrefix}.${n}`, depth));
}

export const AssistantMarkdown = ({ text }) => {
    const content = useMemo(() => renderBlocks(text || '', 'md'), [text]);
    return <>{content}</>;
};
